import {useAuth, useAuthIsNormal, useAuthWhiteLabel, useAuthLookup, useAuthControl, useAuthToken, useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import React, {useLayoutEffect, useMemo} from 'react';
import utils from 'helpers/utils';
import constants from 'helpers/constants';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import {useLocation, useMatches, useParams} from 'react-router-dom';
import Navigate from 'components/organisms/Utils/Navigate/Navigate';
import system from 'helpers/system';
import {useEffectItem} from 'helpers/hooks/utils';

export const AuthRoute = ({ children }) => {
  const auth = useAuth();
  const token = useAuthToken();
  const whiteLabel = useAuthWhiteLabel();
  const normal = useAuthIsNormal();
  const authControl = useAuthControl();
  const location = useLocation();
  const params = useParams();
  const matches = useMatches();
  const snackbar = useSnackbar();

  const authorize = useAuthorize();

  const paramsMemo = useEffectItem(params);
  const matchesMemo = useEffectItem(matches);

  const authContext = useMemo(() => {
    const match = matchesMemo?.find((m) => {
      return utils.comparePath(m.pathname, location.pathname) &&
        utils.isDefined(m.handle?.auth);
    });

    const authContext = match?.handle?.auth ? (
      utils.isFunction(match.handle.auth) ? match.handle.auth() : {...match.handle.auth}
    ) : utils.createAuth();

    authContext.meta = utils.isFunction(authContext.meta) ? authContext.meta(match) :
      (authContext.meta ?? {...match?.params});

    authContext.attribute = authContext.attribute ? utils.resolveAttribute(authContext.attribute, paramsMemo) : authContext.attribute;

    return authContext;
  }, [matchesMemo, location.pathname, paramsMemo]);

  const lookupData = useAuthLookup(authContext.meta?.$lookup);
  authContext.meta = useMemo(() => {
    return {
      ...authContext.meta,
      ...lookupData
    };
  }, [authContext.meta, lookupData]);

  const origin = window.location.origin;
  const isLogin = utils.comparePath(location.pathname, '/auth/login')
  useLayoutEffect(() => {
    if (Boolean(whiteLabel)) {
      const whiteLabelDef = constants.data.lookup('whiteLabels', whiteLabel);
      const whiteLabelHost = system.webHost(whiteLabel);
      if (whiteLabelDef && whiteLabelHost) {
        utils.whiteLabelRedirect(window.location, whiteLabelHost, token, auth?.loggedIn);
      }
    }
  }, [origin, whiteLabel, isLogin, auth?.loggedIn, token]);

  if (!authControl.suspended) {
    const valid = authorize(authContext);

    if (!valid) {
      if (!auth?.loggedIn) {
        return <Navigate to='/auth/login'
                         state={{
                           from: utils.comparePath(location.pathname, '/auth/login') ? location?.state?.from : location,
                           resetSearchParams: true
                         }}
                         replace/>
      } else if (!normal) {
        snackbar.show('You are not authorized to see this information. You will redirected to the login page.', null,
          {color: 'warning', autoHideDuration: constants.delay.warning});

        return <Navigate to='/auth/logout'
                         state={{
                           from: utils.comparePath(location.pathname, '/auth/logout') ? location?.state?.from : location,
                           resetSearchParams: true
                         }}
                         replace/>
      } else if (!utils.comparePath(location.pathname, '/')) {
        snackbar.show('You are not authorized to see this information. You are redirected to an allowed page.', null,
          {color: 'warning', autoHideDuration: constants.delay.warning});

        return <Navigate to='/'
                         state={{
                           from: location,
                           resetSearchParams: true
                         }}
                         replace={true}/>
      } else {
        return <Navigate to='/error/forbidden'
                         state={{
                           from: location,
                           error: {
                             status: constants.http.status.forbidden,
                             statusText: 'This page is not allowed'
                           }
                         }}
                         replace/>
      }
    }
  }

  return <React.Fragment>{children}</React.Fragment>
};


AuthRoute.propTypes = {
}

export default AuthRoute;
